/**
 * Standardized effect sizes and the conversions between them.
 *
 * Formulas, with sources:
 * - Cohen's d (independent groups): (M₁ − M₂) / sₚ, sₚ = √(((n₁ − 1)s₁² + (n₂ − 1)s₂²)
 *   / (n₁ + n₂ − 2)). Cohen (1988).
 * - Hedges' g: J × d with the small-sample correction J = 1 − 3 / (4(n₁ + n₂) − 9),
 *   i.e. 1 − 3/(4·df − 1). Hedges & Olkin (1985); Borenstein et al. (2009).
 * - Glass's Δ: (M₁ − M₂) / s_control. Glass (1976).
 * - Paired designs: d_z = M_diff / s_diff and d_av = M_diff / ((s₁ + s₂) / 2).
 *   Lakens (2013).
 * - d from t: t√(1/n₁ + 1/n₂) for independent groups, t / √n for paired data (= d_z).
 * - Partial η² from F: F·df₁ / (F·df₁ + df₂); from sums of squares,
 *   η² = SS_effect / SS_total and partial η² = SS_effect / (SS_effect + SS_error).
 *   Cohen's f = √(η² / (1 − η²)).
 * - r ↔ d: d = 2r / √(1 − r²) and r = d / √(d² + 4), the equal-group-size forms.
 * - Odds ratio ↔ d, logit method: d = ln(OR) × √3 / π. Chinn (2000).
 */

export function pooledStandardDeviation(
  sd1: number,
  n1: number,
  sd2: number,
  n2: number
): number {
  return Math.sqrt(
    ((n1 - 1) * sd1 * sd1 + (n2 - 1) * sd2 * sd2) / (n1 + n2 - 2)
  );
}

/** Hedges' small-sample correction for df = n₁ + n₂ − 2. */
export function hedgesCorrection(df: number): number {
  return 1 - 3 / (4 * df - 1);
}

export interface IndependentEffect {
  meanDifference: number;
  pooledSd: number;
  d: number;
  correction: number;
  g: number;
  /** (M₁ − M₂) divided by the control group's SD. */
  glassDelta: number;
}

export function independentEffect(
  m1: number,
  sd1: number,
  n1: number,
  m2: number,
  sd2: number,
  n2: number,
  control: 1 | 2
): IndependentEffect {
  const meanDifference = m1 - m2;
  const pooledSd = pooledStandardDeviation(sd1, n1, sd2, n2);
  const d = meanDifference / pooledSd;
  const correction = hedgesCorrection(n1 + n2 - 2);
  return {
    meanDifference,
    pooledSd,
    d,
    correction,
    g: correction * d,
    glassDelta: meanDifference / (control === 1 ? sd1 : sd2),
  };
}

export interface PairedEffect {
  meanDifference: number;
  /** (s₁ + s₂) / 2. */
  averageSd: number;
  dAv: number;
  /** Null when the SD of the differences was not given. */
  dZ: number | null;
}

export function pairedEffect(
  m1: number,
  sd1: number,
  m2: number,
  sd2: number,
  sdDifference: number | null
): PairedEffect {
  const meanDifference = m1 - m2;
  const averageSd = (sd1 + sd2) / 2;
  return {
    meanDifference,
    averageSd,
    dAv: meanDifference / averageSd,
    dZ: sdDifference === null ? null : meanDifference / sdDifference,
  };
}

export function dFromIndependentT(t: number, n1: number, n2: number): number {
  return t * Math.sqrt(1 / n1 + 1 / n2);
}

export function dFromPairedT(t: number, n: number): number {
  return t / Math.sqrt(n);
}

export function partialEtaSquaredFromF(
  f: number,
  dfEffect: number,
  dfError: number
): number {
  return (f * dfEffect) / (f * dfEffect + dfError);
}

export function etaSquaredFromSums(
  ssEffect: number,
  ssError: number,
  ssTotal: number
): { etaSquared: number; partialEtaSquared: number } {
  return {
    etaSquared: ssEffect / ssTotal,
    partialEtaSquared: ssEffect / (ssEffect + ssError),
  };
}

export function cohensF(etaSquared: number): number {
  return Math.sqrt(etaSquared / (1 - etaSquared));
}

export function rToD(r: number): number {
  return (2 * r) / Math.sqrt(1 - r * r);
}

export function dToR(d: number): number {
  return d / Math.sqrt(d * d + 4);
}

/** √3 / π: the logistic distribution's SD relative to the logit scale. */
const LOGIT_TO_D = Math.sqrt(3) / Math.PI;

export function oddsRatioToD(oddsRatio: number): number {
  return Math.log(oddsRatio) * LOGIT_TO_D;
}

export function dToOddsRatio(d: number): number {
  return Math.exp(d / LOGIT_TO_D);
}

export type EffectFamily = "d" | "r" | "eta" | "f";
export type Magnitude = "negligible" | "small" | "medium" | "large";

/** Cohen's (1988) small / medium / large reference points for each family. */
export const COHEN_BENCHMARKS: Record<EffectFamily, [number, number, number]> =
  {
    d: [0.2, 0.5, 0.8],
    r: [0.1, 0.3, 0.5],
    eta: [0.01, 0.06, 0.14],
    f: [0.1, 0.25, 0.4],
  };

export function cohenMagnitude(value: number, family: EffectFamily): Magnitude {
  const [small, medium, large] = COHEN_BENCHMARKS[family];
  const size = Math.abs(value);
  if (size >= large) {
    return "large";
  }
  if (size >= medium) {
    return "medium";
  }
  if (size >= small) {
    return "small";
  }
  return "negligible";
}
